// Shared deadline helper for the site's outbound POST channels (contact form,
// newsletter signup). Adds a hard timeout and honours a caller-supplied
// AbortSignal so a component can cancel an in-flight request on unmount.
//
// Why a dedicated timeout error: a bare `fetch` with no timeout hangs forever
// when the endpoint stalls, leaving the submit button stuck in its disabled
// "submitting" state with no visible error. Callers distinguish an intentional
// caller abort (unmount, stay silent) from a timeout (show a retry-oriented
// message) via the error type — both current callers do exactly that.
//
// Why `withTimeout` takes the whole operation rather than just wrapping fetch:
// `fetch` resolves as soon as the response HEADERS arrive, but the body is
// still streaming. A deadline that stops there leaves `response.json()`
// unbounded, so an endpoint that sends `200` and then stalls mid-body would
// hang exactly like the untimed original. Passing the body read inside the
// operation keeps one deadline over the entire request.

export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export interface TimeoutOptions {
  /** Abort the operation after this many milliseconds. Defaults to 10s. Must be positive and finite. */
  timeoutMs?: number;
  /** Caller's signal; aborting it cancels the operation (e.g. on unmount). */
  signal?: AbortSignal | null;
}

export const DEFAULT_TIMEOUT_MS = 10_000;

/** How far to walk an error's `cause` chain when classifying an abort. */
const MAX_CAUSE_DEPTH = 5;

function abortError(cause?: unknown): DOMException {
  const error = new DOMException('The operation was aborted.', 'AbortError');
  // `cause` is ES2022 and DOMException's constructor takes no options bag, so it
  // is attached through a narrow cast. Callers stay silent on an abort, so
  // without this the operation's REAL error - a TypeError from a malformed body
  // read, a CSP failure landing in the same tick as an unmount - is erased with
  // no console output and no analytics, which is the exact class of failure this
  // module exists to surface.
  if (cause !== undefined) {
    (error as { cause?: unknown }).cause = cause;
  }
  return error;
}

/**
 * True when `error` is this module's deadline breach.
 *
 * Matched on `name` rather than `instanceof TimeoutError` for the same reason
 * {@link isAbortError} does: `instanceof` is per-realm and per-module-instance.
 * A second copy of this module in another chunk, an iframe realm, or a wrapper
 * that re-mints the error would fail the check - and the callers' timeout branch
 * is what writes the anti-duplicate-send cooldown, so a missed classification
 * does not merely show the wrong banner, it drops the guard entirely and the
 * next click resends a message the provider may already have accepted.
 *
 * Returns `boolean`, not `error is TimeoutError`: the check is structural, so
 * narrowing to the class would assert `Error` members it never establishes -
 * and the platform mints its own `DOMException`s named `TimeoutError`
 * (`AbortSignal.timeout()`, some fetch network-deadline paths). Treating those
 * as a deadline breach is the RIGHT call for both callers, which only branch on
 * the answer; claiming they are instances of this class is not.
 */
export function isTimeoutError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'TimeoutError'
  );
}

/**
 * True when `error` was caused by an abort, including when the fetch
 * implementation wrapped it.
 *
 * Checking `error instanceof DOMException && error.name === 'AbortError'` is
 * not enough: aborting mid-body-stream rejects with a wrapper (undici raises
 * `TypeError: terminated` whose `cause` is the AbortError), so a component
 * unmounting during the body read would otherwise be classified as a real
 * failure and try to render an error banner after teardown.
 */
export function isAbortError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
    if (typeof current !== 'object' || current === null) return false;
    // Matched on shape rather than `instanceof DOMException`/`instanceof Error`
    // on purpose: those are per-realm. A DOMException minted in one realm (an
    // iframe, or jsdom's globals under a Node test runner) fails an instanceof
    // check against another realm's constructor, which would silently
    // reclassify an unmount abort as a real failure. `name` is realm-agnostic.
    // `cause` is ES2022 while the compile target is ES2020, so it is read
    // through a narrow cast; a missing property is `undefined` either way.
    const candidate = current as { name?: unknown; cause?: unknown };
    if (candidate.name === 'AbortError') return true;
    if (candidate.cause === undefined) return false;
    current = candidate.cause;
  }
  return false;
}

/**
 * Run `operation` under a single deadline, passing it a signal that aborts on
 * either the timeout or the caller's own signal.
 *
 * - Timeout -> rejects with {@link TimeoutError} (caller surfaces a retryable error).
 * - Caller `signal` aborts -> rejects with the native `AbortError` so the
 *   caller can stay silent (the component is going away).
 * - Anything else the operation throws -> propagates unchanged.
 *
 * The deadline both aborts the operation's signal AND settles this call, rather
 * than aborting and then waiting for the operation to notice. An operation that
 * ignores its signal keeps running - a promise cannot be torn up from the
 * outside - but it can no longer hold the caller hostage, which is the whole
 * point: a submit button that never comes back is the failure this module
 * exists to prevent, and "the fetch polyfill honoured the abort" is not
 * something a form should have to depend on.
 */
export async function withTimeout<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  { timeoutMs = DEFAULT_TIMEOUT_MS, signal: externalSignal }: TimeoutOptions = {},
): Promise<T> {
  // `setTimeout` coerces NaN, 0 and negatives to "fire on the next tick", which
  // would reject every request with a TimeoutError that looks like a stalled
  // endpoint. Fail on the bad argument instead of on the request.
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new RangeError(`timeoutMs must be a positive finite number, received ${String(timeoutMs)}`);
  }

  // Already cancelled: never start the work at all. Starting it and relying on
  // the signal would let a side-effecting operation run after cancellation.
  if (externalSignal?.aborted) {
    throw abortError();
  }

  const controller = new AbortController();

  // Rejects on the deadline or on the caller's abort, independently of whether
  // the operation ever reacts to its signal.
  let settleDeadline!: (error: unknown) => void;
  const deadline = new Promise<never>((_resolve, reject) => {
    settleDeadline = reject;
  });

  // Deadline first, abort second, in both handlers. Aborting first lets a
  // synchronous abort listener inside the operation reject with its own error
  // and win the race, which would report a breached deadline as a generic
  // failure - or an unmount as one.
  const timeoutId = setTimeout(() => {
    settleDeadline(new TimeoutError());
    controller.abort();
  }, timeoutMs);

  // Forward the caller's reason so an operation inspecting `signal.reason` sees
  // why it was cancelled rather than a generic abort.
  const onExternalAbort = () => {
    settleDeadline(abortError());
    controller.abort(externalSignal?.reason);
  };
  externalSignal?.addEventListener('abort', onExternalAbort, { once: true });

  try {
    const attempt = operation(controller.signal);
    // A late settlement of the losing promise must not surface as an unhandled
    // rejection once the race has already been decided.
    attempt.catch(() => undefined);
    const result = await Promise.race([attempt, deadline]);
    // The operation can settle successfully in the same tick the caller
    // cancels, winning the race. Returning the result anyway would hand a
    // torn-down caller a value it must ignore - it would reset the contact form
    // and clear an address the reader may have retyped. This is the ONLY layer
    // enforcing that: the callers deliberately do not repeat the check, because
    // a duplicate guard is one no test can distinguish from this one.
    if (externalSignal?.aborted) {
      throw abortError();
    }
    return result;
  } catch (error) {
    // A caller abort outranks whatever the operation happened to throw: both
    // can land in the same tick, and reporting anything other than an abort
    // would make a torn-down component log and render a banner. Normalised
    // rather than rethrown so callers need only one check.
    if (externalSignal?.aborted) {
      // The operation's own error is preserved as the `cause` rather than
      // dropped: the callers return silently on an abort, so a genuine defect
      // that happens to land in the same tick as an unmount would otherwise
      // leave no trace anywhere.
      throw isAbortError(error) ? error : abortError(error);
    }
    // Everything else - including an abort the operation raised for its own
    // reasons - propagates unchanged. The deadline no longer needs a remap
    // here: it settles the race directly with a TimeoutError, so a timeout can
    // never arrive disguised as the operation's own abort.
    throw error;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener('abort', onExternalAbort);
    }
  }
}
